import { ColumnDef } from '@tanstack/react-table'
import { format } from 'date-fns'
import { useMemo, useState } from 'react'
import { PiDownloadSimple, PiReceipt } from 'react-icons/pi'
import { DataTable, DataTableProvider } from '../../components/DataTable'
import { DataTableContainer } from '../../components/DataTable/UI'
import { IColumnVisibility } from '../../components/DataTable/types'
import { ColumnsStoreProvider } from '../../stores/ColumnsStore/ColumnsStoreProvider'
import useApiService from 'services/api'
import { FbLink } from 'components/FbUI/FbLink'
import { TextPill } from 'components/FbUI/TextPill'
import InvoiceExportModal from 'components/Modals/ExportModal/InvoiceExportModal'
import { ExportButton } from 'components/Buttons/ExportButton'

const TABLE_KEY = 'rebate-payments-table'

function RebatePaymentsTabComponent() {
  const api = useApiService()
  const { data: invoicesData, isFetching } = api.useManufacturerInvoices()
  const [showExportModal, setShowExportModal] = useState(false)

  const columns = useMemo<ColumnDef<any, any>[]>(
    () => [
      {
        accessorKey: 'created',
        header: 'Date',
        size: 150,
        cell: (info) => format(new Date(info.getValue()), 'MMM d, yyyy'),
      },
      {
        accessorKey: 'amount',
        header: 'Invoice Amount',
        size: 150,
        cell: (info) => `$${info.getValue().toFixed(2)}`,
      },
      {
        accessorKey: 'claim_count',
        header: '# of Claims',
        size: 120,
      },
      {
        accessorKey: 'due_date',
        header: 'SLA Date',
        size: 150,
        cell: (info) => format(new Date(info.getValue()), 'MMM d, yyyy'),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        size: 120,
        cell: (info) => {
          const status = info.getValue() as string
          const [bg, text] =
            status === 'PAID'
              ? ['#dcfce7', '#15803d'] // green
              : status === 'OVERDUE'
                ? ['#fee2e2', '#b91c1c'] // red
                : status === 'PENDING'
                  ? ['#fef3c7', '#b45309'] // amber
                  : ['#f3f4f6', '#374151'] // gray
          const label =
            status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()
          return (
            <TextPill backgroundColor={bg} textColor={text}>
              {label}
            </TextPill>
          )
        },
      },
      {
        id: 'actions',
        header: 'Actions',
        size: 150,
        meta: {
          isLastColumn: true,
        },
        cell: (info) => {
          const invoice = info.row.original
          if (invoice.stripe_invoice_url) {
            return (
              <FbLink
                to={invoice.stripe_invoice_url}
                target="_blank"
                title="View Invoice"
              >
                <span className="flex items-center gap-1.5">
                  <PiReceipt size={16} />
                  View Invoice
                </span>
              </FbLink>
            )
          }
          return null
        },
      },
    ],
    []
  )

  return (
    <>
      <DataTableContainer className="h-full">
        <InvoiceExportModal
          show={showExportModal}
          _handleClose={() => setShowExportModal(false)}
        />
        <DataTable
          loading={isFetching}
          data={invoicesData?.results ?? []}
          columns={columns}
          tableKey={TABLE_KEY}
          stickyLastColumn
          footerControls={
            <ExportButton
              variant="secondary"
              tooltip="Download limited to 10,000 rows"
              className="!text-neutral-800 !border-neutral-300 hover:!bg-neutral-100 hover:!text-neutral-900 hover:!border-neutral-300 hover:!shadow-none"
              onClick={() => setShowExportModal(true)}
            >
              <PiDownloadSimple size={18} />
            </ExportButton>
          }
        />
      </DataTableContainer>
    </>
  )
}

export function RebatePaymentsTab({
  defaultColumnVisibility,
}: {
  defaultColumnVisibility?: IColumnVisibility<any>
}) {
  return (
    <ColumnsStoreProvider
      tableKey={TABLE_KEY}
      defaultColumnVisibility={defaultColumnVisibility}
    >
      <DataTableProvider tableKey={TABLE_KEY}>
        <RebatePaymentsTabComponent />
      </DataTableProvider>
    </ColumnsStoreProvider>
  )
}
